/**
 * NMS Homepage Tabs — no-reload switching between alternate index templates,
 * with idle + intent prefetching.
 *
 * Each tab in nms-sale-hero is a real link to /?view=<suffix> (progressive
 * enhancement — works without JS). This controller upgrades it:
 *
 * Responsibilities are strictly separated:
 * - `cache` (Map): completed, parsed template entries — memory only, dies
 *   with the document (refresh/navigation discards it automatically).
 * - `inflight` (Map): one request per view at a time — dedupes clicks,
 *   intent prefetch and the background queue onto the same Promise.
 * - `navToken`: decides which *user navigation* may render — a completed
 *   response can still be cached, but a stale token never touches the DOM.
 *
 * Retrieval: `getView(href, view)` is the single path — cache → inflight →
 * fetch. Used identically by navigation, Back/Forward and prefetch.
 * Prefetching only fills `cache`/`inflight`; it never touches the DOM,
 * history, tabs or loading state.
 *
 * Rendering swaps [data-page-content] (the theme.liquid wrapper around
 * {{ content_for_layout }}) — runScripts() then recreates its scripts, as
 * innerHTML leaves them inert (external files load once per page).
 * Post-swap, `nms:content-updated` is dispatched + initNMSProductCard() runs.
 *
 * Loaded once by sections/nms-sale-hero.liquid when homepage_tab blocks exist.
 */
(function () {
  'use strict';

  // Multiple sections may emit this script tag — initialize once.
  if (window.neemans?.homepageTabs) return;

  // Theme editor: stay as plain links. Background fetches of other views
  // from the preview make the editor jump to that template (e.g. Men → Sale).
  if (window.Shopify?.designMode) return;
  window.neemans = window.neemans || {};
  window.neemans.homepageTabs = true;

  const REGION_SELECTOR = '[data-page-content]';
  const TAB_SELECTOR = 'a[data-homepage-view]';
  const ACTIVE_CLASS = 'nms-sale-hero__tab--active';
  const LOADING_CLASS = 'nms-page-content--loading';

  /** @type {Map<string, {html: string, title: string}>} view -> rendered content */
  const cache = new Map();

  /** @type {Map<string, Promise<{html: string, title: string}>>} view -> in-flight request */
  const inflight = new Map();

  /** Latest navigation token — a stale response must never win. */
  let navToken = 0;

  let currentView = viewFromUrl(window.location.href);

  function viewFromUrl(url) {
    try {
      const u = new URL(url, window.location.origin);
      return (u.searchParams.get('view') || '').trim().toLowerCase();
    } catch {
      return '';
    }
  }

  function getRegion() {
    return document.querySelector(REGION_SELECTOR);
  }

  function setLoading(region, loading) {
    region.classList.toggle(LOADING_CLASS, loading);
    region.setAttribute('aria-busy', loading ? 'true' : 'false');
  }

  /**
   * Seed the cache with the server-rendered view — going back to it must
   * never need a network request, and it must never be fetched twice.
   */
  function seedCurrentView(region) {
    if (!cache.has(currentView)) {
      cache.set(currentView, { html: region.innerHTML, title: document.title });
    }
  }

  /**
   * The single retrieval path — navigation, popstate, intent prefetch and the
   * background queue all funnel through here:
   *   1. cache hit    → resolved immediately
   *   2. in-flight    → the existing Promise is returned (never a 2nd request)
   *   3. otherwise    → fetch, parse, cache on success, clean up inflight
   * Failures leave no cache entry and remove the inflight slot, so a later
   * interaction retries cleanly.
   */
  function getView(href, view) {
    const cached = cache.get(view);
    if (cached) return Promise.resolve(cached);

    const pending = inflight.get(view);
    if (pending) return pending;

    const controller = new AbortController();
    const promise = fetch(href, {
      signal: controller.signal,
      credentials: 'same-origin',
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    })
      .then(async response => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const region = doc.querySelector(REGION_SELECTOR);
        if (!region) throw new Error('Missing page-content region in response');
        return { html: region.innerHTML, title: doc.title };
      })
      .then(entry => {
        cache.set(view, entry);
        return entry;
      })
      .finally(() => inflight.delete(view));

    inflight.set(view, promise);
    return promise;
  }

  /** Script URLs already on the page — a section's script must never run twice. */
  const loadedScripts = new Set(
    Array.from(document.scripts, script => script.src).filter(Boolean)
  );

  const EXECUTABLE_TYPES = ['', 'text/javascript', 'application/javascript', 'module'];

  /**
   * Scripts inserted through innerHTML never run. A view can carry sections
   * the first page didn't have (e.g. the full homepage under Men/Women), so
   * recreate each script: external ones only if not loaded yet, inline ones
   * every time since they bind to the freshly inserted markup.
   */
  function runScripts(region) {
    region.querySelectorAll('script').forEach(oldScript => {
      if (!EXECUTABLE_TYPES.includes(oldScript.type.trim().toLowerCase())) return;
      if (oldScript.src) {
        if (loadedScripts.has(oldScript.src)) return;
        loadedScripts.add(oldScript.src);
      }

      const script = document.createElement('script');
      for (const { name, value } of oldScript.attributes) script.setAttribute(name, value);
      if (!oldScript.src) script.textContent = oldScript.textContent;
      if (oldScript.src) script.async = false; // keep document order between them
      oldScript.replaceWith(script);
    });
  }

  /**
   * Bring newly inserted content up: strip stale `data-initialized` flags so
   * component inits re-run, dispatch the shared NMS re-init event, and wire
   * product cards.
   */
  function reinitRegion(region) {
    region
      .querySelectorAll('[data-initialized]')
      .forEach(el => el.removeAttribute('data-initialized'));

    // Deferred-CSS links (media="print" + onload="this.media='all'") are the
    // theme's standard — but onload doesn't reliably fire for
    // innerHTML-inserted elements on mobile browsers, leaving sections
    // unstyled/blank. The file is already cached, so this is instant.
    region.querySelectorAll('link[media="print"]').forEach(link => {
      link.media = 'all';
    });

    runScripts(region);

    // Safari quirk: <picture><source> inserted via innerHTML fails to
    // evaluate <source media> on WebKit — the <img> never resolves a
    // source and banners render blank (desktop emulation is Blink and
    // unaffected). Cloning the element forces a fresh source evaluation.
    region.querySelectorAll('picture').forEach(picture => {
      picture.replaceWith(picture.cloneNode(true));
    });

    // iOS can stall native lazy-loading for images inserted via innerHTML
    // until a scroll/resize — eager-load the first section's media (the
    // banner the user sees immediately) so it never renders blank.
    const firstSection = region.querySelector('.shopify-section');
    if (firstSection) {
      firstSection
        .querySelectorAll('img[loading="lazy"]')
        .forEach(img => img.setAttribute('loading', 'eager'));
    }

    document.dispatchEvent(new CustomEvent('nms:content-updated'));
    if (typeof window.initNMSProductCard === 'function') window.initNMSProductCard();
  }

  /** Reflect the current view on every homepage tab (post-swap markup too). */
  function syncTabs(view) {
    document.querySelectorAll(TAB_SELECTOR).forEach(tab => {
      const active = (tab.dataset.homepageView || '') === view;
      tab.classList.toggle(ACTIVE_CLASS, active);
      tab.setAttribute('aria-selected', active ? 'true' : 'false');
      if (active) {
        tab.setAttribute('aria-current', 'page');
      } else {
        tab.removeAttribute('aria-current');
      }
    });
  }

  async function navigate(href, view, { push = true } = {}) {
    const region = getRegion();
    if (!region || view === currentView) return;

    const token = ++navToken;
    // Cached views swap instantly — only show the loading state while we
    // actually await the network (first visit or click-during-prefetch).
    if (!cache.has(view)) setLoading(region, true);

    try {
      const entry = await getView(href, view);
      if (token !== navToken) return; // superseded — entry is cached, DOM untouched

      region.innerHTML = entry.html;
      if (entry.title) document.title = entry.title;
      currentView = view;
      if (push) history.pushState({ view }, '', href);

      syncTabs(view);
      reinitRegion(region);
      window.scrollTo(0, 0);
    } catch (error) {
      console.warn(
        '[NMS] Homepage tab navigation failed — the current page is kept intact.',
        error
      );
    } finally {
      if (token === navToken) setLoading(region, false);
    }
  }

  /* ------------------------------------------------
     Prefetching — background queue + user intent
     ------------------------------------------------ */

  /** Connection-aware: skip only automatic background prefetch on
      save-data / slow links; clicks and intent prefetch always work. */
  function canBackgroundPrefetch() {
    const conn = navigator.connection;
    if (!conn) return true; // Safari — no API, just go
    if (conn.saveData) return false;
    return !['slow-2g', '2g'].includes(conn.effectiveType);
  }

  /**
   * Alternate views are discovered from the rendered tabs — never hardcoded —
   * so blocks added/renamed in the editor are picked up automatically.
   */
  function discoverViews() {
    const views = [];
    const seen = new Set();
    document.querySelectorAll(TAB_SELECTOR).forEach(link => {
      const view = link.dataset.homepageView || '';
      if (!link.href || seen.has(view)) return;
      if (view === currentView || cache.has(view) || inflight.has(view)) return;
      seen.add(view);
      views.push({ view, href: link.href });
    });
    return views;
  }

  /**
   * Sequential idle prefetch — one view at a time so the queue never
   * competes with itself, and a failed view doesn't stop the rest. User
   * clicks bypass this entirely (getView starts requests immediately).
   */
  async function prefetchViews() {
    if (!canBackgroundPrefetch()) return;
    for (const { view, href } of discoverViews()) {
      try {
        await getView(href, view);
      } catch {
        /* background failure — silent; the view retries on user intent */
      }
    }
  }

  function schedulePrefetch() {
    if ('requestIdleCallback' in window) {
      requestIdleCallback(prefetchViews, { timeout: 3000 });
    } else {
      setTimeout(prefetchViews, 2000);
    }
  }

  /**
   * Intent prefetch — hover/focus/press warms the view through the same
   * getView() path. Delegated on document so it survives the tabs being
   * swapped with the page content.
   */
  function intentPrefetch(event) {
    // Touch-scroll can dispatch pointer events with a non-Element target
    // (document/text node) — closest() only exists on Element.
    if (!(event.target instanceof Element)) return;

    const link = event.target.closest(TAB_SELECTOR);
    if (!link || !link.href) return;

    const view = link.dataset.homepageView || '';
    if (view === currentView || cache.has(view) || inflight.has(view)) return;

    getView(link.href, view).catch(() => {}); // silent — failures retry later
  }

  ['pointerover', 'focusin', 'pointerdown'].forEach(type =>
    document.addEventListener(type, intentPrefetch, { passive: true })
  );

  // Delegated click — persists across content swaps since it lives on document.
  document.addEventListener('click', event => {
    if (
      event.defaultPrevented ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey ||
      event.button !== 0
    ) {
      return;
    }

    if (!(event.target instanceof Element)) return;

    const link = event.target.closest(TAB_SELECTOR);
    if (!link || !link.href) return;

    event.preventDefault();
    navigate(link.href, link.dataset.homepageView || '');
  });

  // Back/Forward — restore without pushing a new history entry.
  window.addEventListener('popstate', () => {
    navigate(window.location.href, viewFromUrl(window.location.href), { push: false });
  });

  // Seed the server-rendered view immediately — it's never fetched again —
  // then let the background queue warm the alternates once the browser idles.
  const initialRegion = getRegion();
  if (initialRegion) {
    seedCurrentView(initialRegion);
    schedulePrefetch();
  }
})();
